import type { GenerateRequest, GenerationJob } from '@studio/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStudioClient } from '../context';
import { parseApiError } from '../errors';

/**
 * `POST /api/generate` — submits a generation request to the backend. The
 * backend runs the appropriate LLM agent (Enhance / Scene Match) then calls
 * the BFL ImageProvider; both fall back gracefully when their keys are absent.
 *
 * On success, invalidates:
 * - `['generations']` so the Gallery + Studio recent-drafts strip refetch.
 * - `['creditBalance']` so the credits chip reflects the just-debited balance.
 */
export function useGenerate() {
  const client = useStudioClient();
  const qc = useQueryClient();
  return useMutation<GenerationJob, Error, GenerateRequest>({
    mutationFn: async (body) => {
      const res = await client.api.generate.$post({ json: body });
      if (!res.ok) throw await parseApiError(res);
      return (await res.json()) as GenerationJob;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['generations'] });
      qc.invalidateQueries({ queryKey: ['creditBalance'] });
    },
  });
}
